import { Grid, GridProps, styled, Typography } from '@mui/material'
import MonetaryFormat from '../MonetaryFormat'
import { useTranslation } from 'react-i18next'
import { currency } from '../../utils/currency'
import { TOrderPrice } from './interface'
import { DiscountDetailLine } from './ItemDetailAndPrice'
import { VoidDisplay } from './VoidDisplay'
import { useCustomerFacingDisplayContext } from 'pages/CustomerFacingDisplayPage/context/CustomerFacingDisplayProvider'

const GridSummary = styled((props: GridProps) => <Grid container {...props} />)(
    ({ theme }) => ({
        paddingTop: theme.spacing(1),
        borderTop: '2px solid  ' + theme.palette.background.default,
        paddingBottom: theme.spacing(1),
    }),
)

const GridLine = styled((props: GridProps) => (
    <Grid item container justifyContent="space-between" {...props} />
))(({ theme }) => ({
    paddingBottom: theme.spacing(0),
}))

const GridLineFinal = styled((props: GridProps) => (
    <Grid item container justifyContent="space-between" {...props} />
))(({ theme }) => ({
    paddingBottom: theme.spacing(1),
}))

const GridLineTotal = styled((props: GridProps) => (
    <Grid item container justifyContent="space-between" {...props} />
))(({ theme }) => ({
    paddingTop: theme.spacing(1),
    borderTop: '2px solid  ' + theme.palette.background.default,
    paddingBottom: theme.spacing(0),
    color: theme.palette.primary.main,
}))

const OrderSummary = ({
    subtotal,
    totalDiscount,
    totalServiceCharge,
    totalTax,
    totalExclusiveTax,
    totalInclusiveTax,
    totalDue,
    serviceChargeRate,
    orderDiscounts,
    orderComp: compDiscount,
    orderVoid,
}: TOrderPrice) => {
    const { t } = useTranslation()
    // const { currency } = useFormat()
    const { operationalSetting } = useCustomerFacingDisplayContext()
    const { currencyCode, language } = operationalSetting

    const displayTax = (
        orderTotals:
            | Pick<
                  TOrderPrice,
                  'totalTax' | 'totalInclusiveTax' | 'totalExclusiveTax'
              >
            | undefined,
    ): string => {
        if (
            orderTotals?.totalInclusiveTax!! > 0 &&
            orderTotals?.totalExclusiveTax!! > 0
        ) {
            return `${currency(
                orderTotals?.totalTax ?? 0,
                language,
                currencyCode ?? 'USD',
            )} (${currency(
                orderTotals?.totalInclusiveTax ?? 0,
                language,
                currencyCode ?? 'USD',
            )} INCL.) `
        } else {
            return `${currency(
                orderTotals?.totalTax ?? 0,
                language,
                currencyCode ?? 'USD',
            )}`
        }
    }

    return (
        <GridSummary direction={'column'}>
            {(orderDiscounts?.length ?? 0) > 0 && (
                <Grid item>
                    {orderDiscounts?.map((discount) => {
                        return <DiscountDetailLine {...discount} />
                    })}
                </Grid>
            )}
            {compDiscount && (
                <Grid item>
                    <DiscountDetailLine {...compDiscount} />
                </Grid>
            )}
            {orderVoid && (
                <Grid item pb={2}>
                    <VoidDisplay {...orderVoid} />
                </Grid>
            )}
            <GridLine>
                <Typography>{t('quick_sell.label.sub_total')}: </Typography>
                <Typography>
                    {currency(subtotal ?? 0, language, currencyCode ?? 'USD')}
                </Typography>
            </GridLine>
            <GridLine>
                <Typography>{t('quick_sell.label.discount')}: </Typography>
                <MonetaryFormat
                    forceMonetaryFormat="minus"
                    value={totalDiscount ?? 0}
                />
            </GridLine>
            {(serviceChargeRate || 0) !== 0 && (
                <GridLine>
                    <Typography>
                        {`${t(
                            'quick_sell.label.service_charge',
                        )} (${serviceChargeRate}%): `}
                    </Typography>
                    <Typography>
                        {currency(
                            totalServiceCharge ?? 0,
                            language,
                            currencyCode ?? 'USD',
                        )}
                    </Typography>
                </GridLine>
            )}
            <GridLineFinal>
                <Typography>{t('quick_sell.label.tax')}: </Typography>
                <Typography>
                    {displayTax({
                        totalTax,
                        totalExclusiveTax,
                        totalInclusiveTax,
                    })}
                </Typography>
            </GridLineFinal>
            <GridLineTotal
                sx={(x) => ({
                    p: 1,
                    background: x.palette.primary.main,
                    color: x.palette.primary.contrastText,
                })}
            >
                <Typography>{t('quick_sell.label.total_due')}: </Typography>
                <Typography>
                    {currency(totalDue ?? 0, language, currencyCode ?? 'USD')}{' '}
                </Typography>
            </GridLineTotal>
        </GridSummary>
    )
}

export default OrderSummary
